export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  OPERATOR: 'operator',
  CLIENT: 'client'
};

export const ROLE_HIERARCHY = {
  [USER_ROLES.SUPER_ADMIN]: {
    level: 4,
    name: 'Super Administrator',
    scope: 'global', // Can access all agencies
    canManage: [USER_ROLES.ADMIN, USER_ROLES.OPERATOR, USER_ROLES.CLIENT],
    canCreate: [USER_ROLES.ADMIN, USER_ROLES.OPERATOR, USER_ROLES.CLIENT],
    hasFullAccess: true
  },
  [USER_ROLES.ADMIN]: {
    level: 3,
    name: 'Administrator',
    scope: 'agency', // Scoped to one agency
    canManage: [USER_ROLES.OPERATOR, USER_ROLES.CLIENT],
    canCreate: [USER_ROLES.OPERATOR, USER_ROLES.CLIENT],
    hasFullAccess: false // Full access within their agency only
  },
  [USER_ROLES.OPERATOR]: {
    level: 2,
    name: 'Operator',
    scope: 'agency', // Scoped to one agency
    canManage: [USER_ROLES.CLIENT],
    canCreate: [],
    hasFullAccess: false,
    requiresAssignments: true
  },
  [USER_ROLES.CLIENT]: {
    level: 1,
    name: 'Client',
    scope: 'agency', // Scoped to one agency
    canManage: [],
    canCreate: [],
    hasFullAccess: false
  }
};

export const getUserRole = (user) => {
  if (user.is_super_admin) return USER_ROLES.SUPER_ADMIN;
  if (user.is_admin) return USER_ROLES.ADMIN;
  if (user.is_operator) return USER_ROLES.OPERATOR;
  return USER_ROLES.CLIENT;
};

export const hasPermission = (userRole, requiredRole) => {
  const userLevel = ROLE_HIERARCHY[userRole]?.level || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole]?.level || 0;
  return userLevel >= requiredLevel;
};

export const canManageRole = (managerRole, targetRole) => {
  return ROLE_HIERARCHY[managerRole]?.canManage?.includes(targetRole) || false;
};

export const canCreateRole = (creatorRole, targetRole) => {
  return ROLE_HIERARCHY[creatorRole]?.canCreate?.includes(targetRole) || false;
};

export const canViewEntity = (userRole, userAssignments, entityType, entityId) => {
  if (ROLE_HIERARCHY[userRole]?.hasFullAccess) return true;

  if (userRole === USER_ROLES.OPERATOR) {
    if (!userAssignments || !userAssignments[entityType]) return false;
    return userAssignments[entityType].includes(entityId);
  }

  return false;
};

/**
 * Check if a role has global scope (can access all agencies)
 */
export const hasGlobalScope = (userRole) => {
  return ROLE_HIERARCHY[userRole]?.scope === 'global';
};

/**
 * Check if a role is agency-scoped (limited to one agency)
 */
export const isAgencyScoped = (userRole) => {
  return ROLE_HIERARCHY[userRole]?.scope === 'agency';
};

/**
 * Check if a user can access a resource in a specific agency
 * Super admins can access any agency
 * Agency-scoped users can only access their own agency
 */
export const canAccessAgency = (user, resourceAgencyId) => {
  const userRole = getUserRole(user);

  // Super admins have global access
  if (hasGlobalScope(userRole)) {
    return true;
  }

  // Agency-scoped users can only access their own agency
  if (isAgencyScoped(userRole)) {
    const userAgencyId = user.agency_id;
    if (!userAgencyId) return false;

    // Convert to strings for comparison
    return String(userAgencyId) === String(resourceAgencyId);
  }

  return false;
};

/**
 * Get the agency context for a user
 * Returns agency_id for agency-scoped users, null for super admins
 */
export const getUserAgencyContext = (user) => {
  const userRole = getUserRole(user);

  if (hasGlobalScope(userRole)) {
    return null; // Super admins don't have a fixed agency context
  }

  return user.agency_id || null;
};
